import React, { useContext, useEffect, useState } from "react";
import { FormNameType, ProcedureType, TScreen, UploadTypeType } from "api/type";

// Import Components
import { NormalText } from "components/index";

// Import Contexts
import ScreenContext from "contexts/screen";
import { ClinicContext, SessionContext } from "contexts";

import ScreenScan from "./ScreenScan";
import ScreenVideo from "./ScreenVideo";
import ScreenVisualize from "./ScreenVisualize";
import ScreenScanInstructions from "./ScreenScanInstructions";
import {
  fetchImagePresignedUrl,
  postPatientsImagesBatch,
  postTransformsBatch,
  submitAdvertiseForm,
  TPostPatientsImagesBatchRequest,
  TPostPatientsImagesBatchResponse,
  TPostTransformsBatchRequest,
  TPostTransformsBatchResponse,
  TSubmitAdvertiseFormRequest,
} from "api/clinicos";
import { isAISupportedProcedure } from "utils/isAISuportedProcedureAndArea";
import { usePoseLandmarker, useUserLocation } from "utils/customHook";
import ScreenVisualizeNonAISupport from "./ScreenVisualizeNonAISupport";
import { mixpanelTrack } from "utils/mixpanel";
import { generateUUID } from "utils/generateUUID";
import { TImageBlobAndViewType } from "components/scan/Scan";
export type TUrlParamsInfoOnboardingScan = {
  name: string | null;
  email: string | null;
  phone: string | null;
};

type RootOnboardingScanProps = {};

const RootOnboardingScan: React.FC<RootOnboardingScanProps> = () => {
  // Context and State Declarations
  const session = useContext(SessionContext);
  const clinic = useContext(ClinicContext);

  // Custom Hooks
  const poseLandmarker = usePoseLandmarker();
  const fetchUserLocation = useUserLocation(session);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [screen, setScreen] = useContext(ScreenContext);
  const [selectedProcedure, setSelectedProcedure] =
    useState<ProcedureType | null>(null);
  const [urlParamsInfo, setUrlParamsInfo] =
    useState<TUrlParamsInfoOnboardingScan | null>(null);
  const [selectedBlobAndPresignedUrl, setSelectedBlobAndPresignedUrl] =
    useState<TImageBlobAndViewType | null>(null);
  const [postTransformsBatchResponse, setPostTransformsBatchResponse] =
    useState<TPostTransformsBatchResponse | null>(null);
  const [isInvalidProcedureValue, setIsInvalidProcedureValue] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

  // Effect: Set Screen to Video Screen
  useEffect(() => {
    setScreen(TScreen.VIDEO_SCREEN);
  }, [setScreen]);

  // Effect: Listen for window messages
  useEffect(() => {
    const currentUrl = window.location.search;
    const urlSearchParams = new URLSearchParams(currentUrl);
    const name = urlSearchParams.get("name");
    const email = urlSearchParams.get("email");
    const phone = urlSearchParams.get("phone");
    const procedure = urlSearchParams.get("procedure");

    if (!procedure) {
      setErrorMessage("No procedure selected");
      return;
    }
    if (!Object.values(ProcedureType).includes(procedure as ProcedureType)) {
      setIsInvalidProcedureValue(true);
      return;
    }
    setSelectedProcedure(procedure as ProcedureType);
    setUrlParamsInfo({ name, email, phone });
  }, []);

  // Effect: Fetch After Image URLs
  useEffect(() => {
    const fetchAfterImageUrls = async () => {
      try {
        if (
          !session ||
          !clinic ||
          !selectedBlobAndPresignedUrl ||
          !selectedProcedure
        )
          return;

        let beforeImageUrl = "";

        // Handle image upload and retrieval
        const body: TPostPatientsImagesBatchRequest = {
          images: [
            {
              view_type: selectedBlobAndPresignedUrl.viewType,
              procedure_types: [selectedProcedure],
            },
          ],
        };
        const result: TPostPatientsImagesBatchResponse =
          await postPatientsImagesBatch(session, body, [
            selectedBlobAndPresignedUrl.image,
          ]);
        if (result.presigned_urls.length !== 1) {
          throw new Error("Invalid number of presigned urls");
        }

        if (!isFormSubmitted) {
          await fetchUserLocation();
          const bodySecond: TSubmitAdvertiseFormRequest = {
            ...(result.batch_id ? { batch_id: result.batch_id } : {}),
            form_user_id: generateUUID(),
            form_name: FormNameType.ONBOARDING_SCAN,
            is_debug: process.env.REACT_APP_ENV === "production" ? false : true,
            form_data: {
              name: urlParamsInfo?.name || "Not captured",
              phone: urlParamsInfo?.phone || "Not captured",
            },
            patient_record: {
              upload_type: UploadTypeType.SCAN,
              location: await fetchUserLocation(),
              first_name: urlParamsInfo?.name?.split(" ")[0] || "",
              last_name:
                urlParamsInfo?.name?.split(" ").slice(1).join(" ") || "",
              phone: urlParamsInfo?.phone || "",
              ...(urlParamsInfo?.email ? { email: urlParamsInfo.email } : {}),
              ...(selectedProcedure
                ? { procedures_of_interest: [selectedProcedure] }
                : {}),
            },
          };
          await submitAdvertiseForm(session, bodySecond);
          setIsFormSubmitted(true);
        }

        beforeImageUrl = result.presigned_urls[0].get_url;
        let secondResult: TPostTransformsBatchResponse | null = null;
        if (isAISupportedProcedure(selectedProcedure)) {
          const secondBody: TPostTransformsBatchRequest = {
            batch_id: result.batch_id,
            request: [
              {
                image_url: beforeImageUrl,
                procedure_types: [selectedProcedure],
                procedure_view: selectedBlobAndPresignedUrl.viewType,
              },
            ],
          };
          secondResult = await postTransformsBatch(session, secondBody);
          setPostTransformsBatchResponse(secondResult);
        }

        // Send the data to the TopAes
        if (!isFormSubmitted) {
          const presignedUrl =
            secondResult?.response[0].presigned_urls.slice(-1)[0] || null;
          const imageBlobPresignedUrl = presignedUrl
            ? await fetchImagePresignedUrl(presignedUrl)
            : null;

          window.parent.postMessage(
            [
              {
                viewType: selectedBlobAndPresignedUrl.viewType,
                beforeImage: selectedBlobAndPresignedUrl.image,
                afterImage: imageBlobPresignedUrl,
              },
            ],
            "*",
          );
        }
        // Send the data to the TopAes

        if (document.referrer.includes("staging")) return;
        mixpanelTrack("Simulation Complete", [
          {
            propertyName: "Clinic Name",
            propertyValue: clinic.clinic_name,
          },
          {
            propertyName: "Procedure",
            propertyValue: selectedProcedure,
          },
          {
            propertyName: "Photo Capture Type",
            propertyValue: "Body Scan",
          },
        ]);
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes("401")) {
            setErrorMessage(
              "Session has expired. Page will refresh in 3 seconds.",
            );
            setTimeout(() => {
              window.location.reload();
            }, 3000);
          } else {
            setErrorMessage(error.message);
          }
        } else {
          setErrorMessage("Error fetching after image urls");
        }
      }
    };

    fetchAfterImageUrls();

    // NOTE: It is safe to not include selectedImageUrlOrBlob in the dependency array
    // Including it will cause a bug where the user goes back to the SelectModel screen,
    // selects a procedure, and then the Visualize screen shows 2 simulations.
    // eslint-disable-next-line
  }, [selectedBlobAndPresignedUrl, selectedProcedure, session]);

  // Helper Functions
  const handleSaveBlobAndPresignedUrl = (
    blobAndPresignedUrl: TImageBlobAndViewType | null,
  ) => {
    setSelectedBlobAndPresignedUrl(blobAndPresignedUrl);
  };

  const handleSaveSelectedProcedure = (procedure: ProcedureType) => {
    setSelectedProcedure(procedure);
  };

  if (isInvalidProcedureValue) {
    return (
      <div style={{ padding: "20px" }}>
        <NormalText textContent="Invalid procedure, please check the documentation for a list of valid procedures" />
        <a
          href="https://github.com/ClinicOS-AI/integration-guide/blob/main/photo_capture.md"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://github.com/ClinicOS-AI/integration-guide/blob/main/photo_capture.md
        </a>
      </div>
    );
  }

  // Render Screen based on the current context
  const renderScreen = () => {
    switch (screen) {
      case TScreen.VIDEO_SCREEN:
        return <ScreenVideo selectedProcedure={selectedProcedure} />;
      case TScreen.BODY_SCAN_INSTRUCTIONS_SCREEN:
        return <ScreenScanInstructions selectedProcedure={selectedProcedure} />;
      case TScreen.BODY_SCAN_SCREEN:
        return (
          <ScreenScan
            selectedProcedure={selectedProcedure}
            poseLandmarker={poseLandmarker}
            handleSaveBlobAndPresignedUrl={handleSaveBlobAndPresignedUrl}
          />
        );
      case TScreen.SIMULATION_VIEW_SCREEN_NON_AI_SUPPORT:
        return (
          <ScreenVisualizeNonAISupport
            urlParamsInfo={urlParamsInfo}
            selectedProcedure={selectedProcedure}
            handleSaveSelectedProcedure={handleSaveSelectedProcedure}
            handleSaveBlobAndPresignedUrl={handleSaveBlobAndPresignedUrl}
          />
        );
      case TScreen.SIMULATION_VIEW_SCREEN:
        return (
          <ScreenVisualize
            selectedProcedure={selectedProcedure}
            postTransformsBatchResponse={postTransformsBatchResponse}
          />
        );
      default:
        return null;
    }
  };

  if (errorMessage) return <NormalText textContent={errorMessage} />;

  return <div id="onboarding-advertise-container">{renderScreen()}</div>;
};

export default RootOnboardingScan;
